import React, { useCallback, useEffect } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import { AxiosError, AxiosResponse } from 'axios'
import _ from 'lodash'

import {
  StyledBasketDrawer,
  StyledDrawerBody,
  StyledDrawerHeader,
  Container,
  Header,
  Row,
  Column,
  StyledIcon,
  StyledClinSuccess,
  StyledClinButton,
  StyledSpinnerContainer,
  StyledClinButtonIcon,
  StyledQuantitySelectorContainer,
  StyledClinInfo,
  StyledClinInfoIconBlock,
  StyledClinUndoButton,
  StyledClinButtonLink,
  StyledFreeShippingContainer,
  StyledShippingText,
  StyledShippingIcon
} from './BasketDrawer.styles'
import { ClinText } from '../../components/ClinText'
import { ClinGroup } from '../../components/ClinGroup'
import { AnnounceMode } from '../../components/ClinAnnounceBar/ClinAnnounceBar'
import { ClinTheme } from '../../ClinTheme'
import { ClinSpacer } from '../../components/ClinSpacer'
import { numberToCurrencyString } from '../../utils/numberToCurrencyString'
import { getBrowserLocale } from '../../utils/getBrowserLocale'
import CheckCircleIcon from '../../assets/media/icons/check-circle-green.svg'
import InfoAccentIcon from '../../assets/media/icons/info-accent.svg'
import {
  ActionType,
  ActionUpdateBasketItemQuantity,
  addBasketItemAsync,
  useBasket
} from '../../context/basket'
import {
  cancelUpdateBasketItemQuantity,
  getCurrentBasket,
  getPriceAndAvailabilityForSku,
  removeItemFromBasket,
  updateBasketItemQuantity
} from '../../services/ApiService'
import { BasketDto, BasketItemDto } from '../../types/swaggerTypes'
import { createAnnounceEvent } from '../../events/AnnounceEvent'
import { useAppContext } from '../../context/app'
import { ClinSpinner } from '../../components/ClinSpinner'
import { BasketViewState } from '../../pages/Basket/Basket'
import { ClinIconPathName } from '../../components/ClinIcon/ClinIcon.paths'
import analyticsServiceSingleton from '../../services/Analytics/initAnalytics'
import { AnalyticsEvent } from '../../services/Analytics'
import { handleBasketError } from '../../utils/basketUtils'
import { ClinTextInput } from '../../components/ClinTextInput'
import { getErrorForBasketItemId } from '../../pages/Basket/Basket.model'
import { IQuantityError } from '../../pages/Basket/BasketContainer'
import { validateQuantity } from '../../utils/validateQuantity'
import { isAusUser } from '../../constants'
import { useFeatureFlags } from '../../context/featureFlags/FeatureFlagContext'
import { FeatureFlagKeys } from '../../context/featureFlags/FeatureFlagKeys'
import FreeShippingIcon from '../../assets/media/free_shipping.svg'

interface IBasketDrawerProps {
  /** Handle closing the confirmation */
  handleClose?: () => void
  handleCheckoutOpen?: (d: boolean) => void
}

// Helper functions to reduce complexity
const createBasketErrorHandler = (
  dispatch: any,
  errorMessage: string,
  retryFn?: () => void
) => {
  return (error: any) => {
    const basketError = handleBasketError(error, undefined, retryFn)
    if (!basketError) {
      dispatch(
        createAnnounceEvent(AnnounceMode.Error, `${errorMessage} ${error}`)
      )
    }
  }
}

export const BasketDrawer: React.FC<IBasketDrawerProps> = (props) => {
  const { handleClose, handleCheckoutOpen } = props
  const { dispatch, defaultShippingAddress, portfolioCountryCode } =
    useAppContext()
  const history = useHistory()
  const { t } = useTranslation()
  const [basket, setBasket] = React.useState<BasketDto | undefined>()
  const [basketViewState, setBasketViewState] = React.useState<BasketViewState>(
    BasketViewState.Loading
  )
  const [{ viewBasket }, basketDispatch] = useBasket()

  const [wasItemRemoved, setWasItemRemoved] = React.useState<boolean>(false)
  const [isUpdatingQuantity, setIsUpdatingQuantity] =
    React.useState<boolean>(false)
  const [quantityErrors, setQuantityErrors] = React.useState<IQuantityError[]>(
    []
  )
  const [isFreeShippingBanner, setIsFreeShippingBanner] =
    React.useState<boolean>(false)

  const { useFeatureFlag } = useFeatureFlags()
  const enableNewCheckoutDrawer = useFeatureFlag(
    FeatureFlagKeys.NewGaCheckoutDrawer
  )

  const handleSelectProduct = (basketItem: any) => {
    handleClose && handleClose()
    history.push(
      `/product/${basketItem.skuCatalogItem.parentCatalogDocumentId}/variant/${basketItem.sku}`
    )
    history.push({
      pathname: `/product/${basketItem.skuCatalogItem.parentCatalogDocumentId}/variant/${basketItem.sku}`,
      state: { isNavigatedFromBasket: true }
    })
    localStorage.removeItem('recentlyRemovedBasketItem')
  }

  const handleGoToBasket = () => {
    handleClose && handleClose()
    analyticsServiceSingleton.trackEvent(AnalyticsEvent.ViewBasket, {
      variant: 'basket_drawer',
      via_drawer: true
    })
    history.push('/basket')
    localStorage.removeItem('recentlyRemovedBasketItem')
  }

  const handleProceedToCheckout = () => {
    analyticsServiceSingleton.trackEvent(AnalyticsEvent.NavigateToCheckout, {
      variant: 'basket_drawer',
      from_drawer: true
    })

    if (isAusUser(portfolioCountryCode)) {
      handleClose && handleClose()
      history.push('/one-step-checkout')
    } else if (enableNewCheckoutDrawer) {
      handleCheckoutOpen && handleCheckoutOpen(true)
    } else {
      handleClose && handleClose()
      history.push('/checkout')
    }

    localStorage.removeItem('recentlyRemovedBasketItem')
  }

  // Helper function to handle price and availability error
  const handlePriceAndAvailabilityError = (error: any) => {
    dispatch(
      createAnnounceEvent(
        AnnounceMode.Error,
        `An error occurred fetching your basket. ${error}`
      )
    )
  }

  // Extracted function for getting price and availability
  const getPriceAndAvailability = async () => {
    if (!basket?.items?.length || !defaultShippingAddress?.addressId) return

    try {
      const skus = basket.items.map((item) => item.sku)
      const pricingCalls = skus.map((sku) =>
        getPriceAndAvailabilityForSku(
          sku,
          defaultShippingAddress.addressId
        ).then((r) => r.data)
      )

      const results = await Promise.all(pricingCalls)

      basketDispatch({
        type: ActionType.SetCurrentBasket,
        basket: basket,
        priceAndAvailability: results,
        countryCode: portfolioCountryCode
      })
      setBasketViewState(BasketViewState.Basket)
    } catch (error) {
      handlePriceAndAvailabilityError(error)
    }
  }

  // Extracted function for updating basket item quantity
  const performUpdateItemQuantity = (
    basketItem: BasketItemDto,
    quantity: number
  ) => {
    setIsUpdatingQuantity(true)
    cancelUpdateBasketItemQuantity()

    updateBasketItemQuantity(basketItem.basketItemId, quantity, basketItem.sku)
      .then((response) => {
        const updateBasketItem = response.data
        basketDispatch({
          type: ActionType.UpdateItemQuantity,
          basketItem: updateBasketItem,
          quantity: updateBasketItem.quantity
        } as ActionUpdateBasketItemQuantity)
        setIsUpdatingQuantity(false)

        analyticsServiceSingleton.trackEvent(AnalyticsEvent.BasketUpdated, {
          action: 'update',
          variant: 'basket_drawer',
          sku: basketItem.sku,
          quantity: quantity
        })
      })
      .catch(
        createBasketErrorHandler(
          dispatch,
          'An error occurred updating an item quantity.',
          () => handleChangeQuantity(basketItem, quantity, true)
        )
      )
  }

  const updateItemQuantityDebounced = useCallback(
    _.debounce((basketItem: BasketItemDto, quantity: number) => {
      if (basketViewState === BasketViewState.Loading) {
        cancelUpdateBasketItemQuantity()
      }
      performUpdateItemQuantity(basketItem, quantity)
    }, 500),
    [basketViewState]
  )

  const validateQuantityAndManageError = (
    basketItem: BasketItemDto,
    quantity: number
  ): boolean => {
    const errMsg = validateQuantity(
      quantity,
      basketItem.skuCatalogItem.item.minimumOrderQuantity ?? 1,
      basketItem.skuCatalogItem.item.maximumOrderQuantity,
      basketItem.skuCatalogItem.item.incrementalOrderQuantity
    )
    if (errMsg) {
      setQuantityErrors([
        ...quantityErrors,
        { basketItemId: basketItem.basketItemId, message: errMsg }
      ])
      return false
    }
    setQuantityErrors([
      ...quantityErrors.filter(
        (err) => err.basketItemId !== basketItem.basketItemId
      )
    ])
    return true
  }

  const handleChangeQuantity = (
    basketItem: BasketItemDto,
    quantity: number,
    isRetry?: boolean
  ) => {
    if (isRetry) {
      updateItemQuantityDebounced(basketItem, quantity)
      return
    }

    if (
      basketViewState !== BasketViewState.Loading &&
      validateQuantityAndManageError(basketItem, quantity)
    ) {
      updateItemQuantityDebounced(basketItem, quantity)
    }
  }

  const handleRemoveItem = (
    basketItem: BasketItemDto,
    isLastBasketItem: boolean
  ) => {
    localStorage.setItem(
      'recentlyRemovedBasketItem',
      JSON.stringify(basketItem)
    )
    setBasketViewState(BasketViewState.Loading)
    removeItemFromBasket(basketItem.basketItemId)
      .then((response) => {
        if (response.status === 204) {
          setWasItemRemoved(true)
          basketDispatch({
            type: ActionType.RemoveBasketItem,
            basketItem
          })
          if (isLastBasketItem) {
            handleClose && handleClose()
          }
          analyticsServiceSingleton.trackEvent(AnalyticsEvent.BasketUpdated, {
            action: 'remove',
            variant: 'basket_drawer',
            sku: basketItem.sku,
            quantity: basketItem.quantity
          })
        }
      })
      .catch(
        createBasketErrorHandler(
          dispatch,
          'An error occurred removing an item from your basket.',
          () => handleRemoveItem(basketItem, isLastBasketItem)
        )
      )
      .finally(() => setBasketViewState(BasketViewState.Basket))
  }

  const handleUndoAction = () => {
    const storedData = localStorage.getItem('recentlyRemovedBasketItem')
    const basketItem = storedData ? JSON.parse(storedData) : null
    const selectedQuantity = basketItem?.quantity ?? 1

    if (basketItem?.priceAndAvailability) {
      addBasketItemAsync(
        basketDispatch,
        basketItem.sku,
        selectedQuantity,
        basketItem.priceAndAvailability,
        []
      )
        .then(() => {
          setBasket(undefined)
          setWasItemRemoved(false)
          setBasketViewState(BasketViewState.Loading)
          localStorage.removeItem('recentlyRemovedBasketItem')
          analyticsServiceSingleton.trackEvent(AnalyticsEvent.BasketAddItem, {
            sku: basketItem.sku,
            quantity: selectedQuantity,
            price: basketItem?.priceAndAvailability?.listPrice,
            currency: basketItem?.priceAndAvailability?.currencyCode
          })
        })
        .catch((error) => {
          handleBasketError(error, undefined, handleUndoAction)
        })
    }
  }

  // Wait for current basket
  useEffect(() => {
    if (!basket?.items?.length) {
      getCurrentBasket()
        .then((response: AxiosResponse<BasketDto> | null) => {
          // Update badge value
          if (response?.data) {
            response.data.items ??= []
            response.data.orders ??= []
            setBasket(response.data)
            basketDispatch({
              type: ActionType.UpdateBasket,
              basket: { data: response.data, isLoading: false }
            })
          }
        })
        .catch((error: AxiosError) => {
          dispatch(
            createAnnounceEvent(
              AnnounceMode.Error,
              `There was an error fetching the basket. ${error}`
            )
          )
        })
    }
  }, [basket, dispatch, basketDispatch])

  // With address and a basket get pricing and availability information
  useEffect(() => {
    if (viewBasket && defaultShippingAddress) {
      void getPriceAndAvailability()
    }
  }, [basket, defaultShippingAddress])

  useEffect(() => {
    if (defaultShippingAddress) {
      setIsFreeShippingBanner(defaultShippingAddress.country === 'GB')
    }
  }, [defaultShippingAddress])

  if (
    !viewBasket?.items?.length &&
    basketViewState !== BasketViewState.Loading
  ) {
    return null
  }

  return (
    <StyledBasketDrawer>
      {basketViewState === BasketViewState.Loading ? (
        <StyledSpinnerContainer>
          <ClinText
            as={'div'}
            display={'block'}
            fontWeight={ClinTheme.fontWeights.medium}
            fontSize={ClinTheme.fontSizes[3]}
            color={ClinTheme.colors.black}
            lineHeight={ClinTheme.lineHeights.heading[1]}
          >
            {t('add_to_basket_confirmation:loading_basket')}
          </ClinText>
          <ClinSpinner size={ClinTheme.space[4]} />
        </StyledSpinnerContainer>
      ) : (
        <>
          <StyledDrawerHeader>
            <ClinGroup justifyContent={'space-between'}>
              <ClinText
                as={'div'}
                fontWeight={ClinTheme.fontWeights.medium}
                fontSize={ClinTheme.fontSizes[3]}
                color={ClinTheme.colors.black}
                lineHeight={ClinTheme.lineHeights.heading[1]}
              >
                {t('add_to_basket_confirmation:basket_title')}
              </ClinText>
            </ClinGroup>
          </StyledDrawerHeader>
          <StyledDrawerBody>
            {wasItemRemoved ? (
              <StyledClinInfo
                marginBottom={ClinTheme.space[2]}
                mode={AnnounceMode.Success}
                title={' '}
              >
                <StyledClinInfoIconBlock>
                  <StyledIcon width={24} height={24} src={InfoAccentIcon} />
                  <ClinText as={'span'} color={ClinTheme.colors.black}>
                    {t('add_to_basket_confirmation:medicine_removed_accounce')}
                  </ClinText>
                </StyledClinInfoIconBlock>
                <StyledClinUndoButton variant="link" onClick={handleUndoAction}>
                  {t('glossary:undo')}
                </StyledClinUndoButton>
              </StyledClinInfo>
            ) : (
              <StyledClinSuccess
                marginBottom={ClinTheme.space[2]}
                mode={AnnounceMode.Success}
                title={' '}
              >
                <StyledIcon width={24} height={24} src={CheckCircleIcon} />
                <ClinText as={'div'} color={ClinTheme.colors.black}>
                  {t('add_to_basket_confirmation:medicine_add_to_basket')}
                </ClinText>
              </StyledClinSuccess>
            )}
            {/* Order details */}
            <Container>
              <Header>
                <Column>
                  <ClinText
                    as={'div'}
                    fontSize={ClinTheme.fontSizes[0]}
                    fontWeight={ClinTheme.fontWeights.medium}
                    lineHeight={ClinTheme.lineHeights.largeParagraph}
                  >
                    {t('add_to_basket_confirmation:clear_label')}
                  </ClinText>
                </Column>
                <Column>
                  <ClinText
                    as={'div'}
                    fontSize={ClinTheme.fontSizes[0]}
                    fontWeight={ClinTheme.fontWeights.medium}
                    lineHeight={ClinTheme.lineHeights.largeParagraph}
                  >
                    {t('add_to_basket_confirmation:quantity_label')}
                  </ClinText>
                </Column>
                <Column>
                  <ClinText
                    as={'div'}
                    fontSize={ClinTheme.fontSizes[0]}
                    fontWeight={ClinTheme.fontWeights.medium}
                    lineHeight={ClinTheme.lineHeights.largeParagraph}
                  >
                    {t('add_to_basket_confirmation:sku')}
                  </ClinText>
                </Column>
                <Column>
                  <ClinText
                    as={'div'}
                    fontSize={ClinTheme.fontSizes[0]}
                    fontWeight={ClinTheme.fontWeights.medium}
                    lineHeight={ClinTheme.lineHeights.largeParagraph}
                    textAlign={'right'}
                  >
                    {t('add_to_basket_confirmation:subtotal')}
                  </ClinText>
                </Column>
              </Header>

              {viewBasket?.items?.map((basketItem) => (
                <Row key={`basket-item-${basketItem.sku}`}>
                  <Column>
                    <ClinText
                      as={'div'}
                      fontSize={ClinTheme.fontSizes[1]}
                      lineHeight={ClinTheme.lineHeights.heading[0]}
                    >
                      <StyledClinButtonIcon
                        iconName={ClinIconPathName.Cross}
                        iconFill={ClinTheme.colors.primary}
                        onClick={() =>
                          handleRemoveItem(
                            basketItem,
                            viewBasket?.items?.length === 1
                          )
                        }
                      />
                    </ClinText>
                  </Column>
                  <Column>
                    <StyledQuantitySelectorContainer
                      unavailable={basketItem?.unavailable}
                    >
                      <ClinTextInput
                        className={'quantity-selector'}
                        disabled={isUpdatingQuantity || basketItem?.unavailable}
                        key={`${basketItem.sku}`}
                        id={`${basketItem.sku}`}
                        type={'number'}
                        name={`quantity-${basketItem.sku}`}
                        min={1}
                        max={
                          basketItem.skuCatalogItem?.item
                            ?.maximumOrderQuantity ?? 10000
                        }
                        step={1}
                        width={'100%'}
                        defaultValue={basketItem.quantity}
                        hasError={
                          quantityErrors &&
                          quantityErrors.filter(
                            (err) =>
                              err.basketItemId === basketItem.basketItemId
                          ).length > 0
                        }
                        prompt={getErrorForBasketItemId(
                          basketItem.basketItemId,
                          quantityErrors
                        )}
                        onChange={(event) =>
                          handleChangeQuantity &&
                          handleChangeQuantity(
                            basketItem,
                            Number.parseInt(event.currentTarget.value, 0)
                          )
                        }
                      />
                    </StyledQuantitySelectorContainer>
                  </Column>
                  <Column>
                    <StyledClinButtonLink
                      variant="link"
                      color={ClinTheme.colors.tealishBlue}
                      onClick={() => handleSelectProduct(basketItem)}
                    >
                      <span>
                        {
                          basketItem.skuCatalogItem?.item
                            ?.genericDosageConcatenation
                        }
                      </span>
                    </StyledClinButtonLink>
                  </Column>
                  <Column>
                    {isUpdatingQuantity ? (
                      <ClinSpinner />
                    ) : (
                      <ClinText
                        as={'div'}
                        fontSize={ClinTheme.fontSizes[1]}
                        fontWeight={ClinTheme.fontWeights.medium}
                        lineHeight={ClinTheme.lineHeights.heading[0]}
                        textAlign={'right'}
                      >
                        {numberToCurrencyString(
                          basketItem.amount,
                          getBrowserLocale(),
                          {
                            style: 'currency',
                            currency:
                              basketItem?.priceAndAvailability?.currencyCode ??
                              viewBasket.currencyCode,
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          }
                        )}
                      </ClinText>
                    )}
                  </Column>
                </Row>
              ))}
            </Container>
            <ClinSpacer hasBorder={true} height={ClinTheme.space[5]} />
            <ClinGroup justifyContent={'space-between'} alignItems={'center'}>
              <ClinText
                as={'div'}
                fontSize={ClinTheme.fontSizes[3]}
                fontWeight={ClinTheme.fontWeights.medium}
                lineHeight={ClinTheme.lineHeights.heading[1]}
              >
                {t('add_to_basket_confirmation:amount_label')}
              </ClinText>
              {isUpdatingQuantity ? (
                <ClinSpinner />
              ) : (
                <ClinText
                  as={'div'}
                  className={'amount'}
                  fontSize={ClinTheme.fontSizes[4]}
                  fontWeight={ClinTheme.fontWeights.medium}
                  lineHeight={ClinTheme.lineHeights.heading[1]}
                >
                  {!viewBasket?.total
                    ? '--'
                    : numberToCurrencyString(
                        viewBasket.total,
                        getBrowserLocale(),
                        {
                          style: 'currency',
                          currency: viewBasket.currencyCode,
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        }
                      )}
                </ClinText>
              )}
            </ClinGroup>

            <ClinSpacer height={ClinTheme.space[5]} />

            {isFreeShippingBanner && (
              <>
                <StyledFreeShippingContainer>
                  <StyledShippingIcon
                    width={408}
                    height={86}
                    src={FreeShippingIcon}
                  />
                  <StyledShippingText
                    fontSize={ClinTheme.fontSizes[2]}
                    fontWeight={ClinTheme.fontWeights.medium}
                  >
                    <Trans
                      i18nKey="add_to_basket_confirmation:free_shipping_banner_text"
                      components={{
                        bold: <strong />
                      }}
                    />
                  </StyledShippingText>
                </StyledFreeShippingContainer>

                <ClinSpacer height={ClinTheme.space[5]} />
              </>
            )}

            <StyledClinButton
              display={'block'}
              variant={'primary'}
              onClick={handleProceedToCheckout}
            >
              {t('add_to_basket_confirmation:checkout')}
            </StyledClinButton>
            <ClinSpacer height={ClinTheme.space[3]} />
            <StyledClinButton
              display={'block'}
              variant={'secondary'}
              onClick={handleGoToBasket}
            >
              {t('add_to_basket_confirmation:view_basket_checkout')}
            </StyledClinButton>
          </StyledDrawerBody>
        </>
      )}
    </StyledBasketDrawer>
  )
}
